import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useQueryClient } from "@tanstack/react-query";

import { shipmentQueryKeys } from "api/queries/shipments/shipmentQueryKeys";
import usePermissions from "hooks/usePermissions";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import { FilterConfig } from "types/Common";

export default function useShipmentFilters(locations: any) {
  const intl = useIntl();
  const { getPaymentLocationLabel } = usePermissions();
  const queryClient = useQueryClient();
  const paymentLocations = useSortedPaymentLocations();

  // for client side filtering we don't have payment location id, so we'll filter with string title instead
  const paymentLocationWithTitle = useMemo(
    () =>
      paymentLocations.map((item) => ({
        ...item,
        value: item.label,
      })),
    [paymentLocations],
  );

  const config: FilterConfig[] = [
    {
      filterType: "multiselect",
      label: getPaymentLocationLabel(),
      key: "payment_location",
      isSearchable: true,
      options: paymentLocationWithTitle,
    },
    {
      filterType: "buttongroup",
      label: "Dangerous goods",
      key: "dangerous_goods",
      options: [
        {
          label: "Dangerous",
          value: true,
        },
        {
          label: "Not dangerous",
          value: false,
        },
      ],
    },
    {
      filterType: "date",
      label: intl.formatMessage({
        id: "general.requestedPickupDate",
        defaultMessage: "Request Pickup date",
      }),
      key: "requested_pickup_date",
    },
    {
      filterType: "date",
      label: intl.formatMessage({
        id: "general.requestedDeliveryDate",
        defaultMessage: "Requested Delivery date",
      }),
      key: "requested_delivery_date",
    },

    {
      filterType: "tabfilter",
      tabType: "multiselect",
      label: "Origin",
      key: "origin",
      isSearchable: true,
      buttonGroupOptions: [
        {
          label: "Address name",
          value: "pickup_address",
        },
        {
          label: "City",
          value: "pickup_city",
        },
      ],
      options: locations.origin,
    },
    {
      filterType: "tabfilter",
      tabType: "multiselect",
      label: "Destination",
      key: "stop1",
      isSearchable: true,
      buttonGroupOptions: [
        {
          label: "Address name",
          value: "stop1_address",
        },
        {
          label: "City",
          value: "stop1_city",
        },
      ],
      options: locations.destination,
    },
    {
      filterType: "text",
      label: intl.formatMessage({ id: "general.createdBy", defaultMessage: "Created by" }),
      key: "created_by",
      ...{
        options: [
          ...new Set(
            queryClient
              .getQueryData<Array<any>>(shipmentQueryKeys.allShipments())
              ?.map((item) => item?.created_by?.name)
              ?.filter((item) => item),
          ),
        ],
      },
    },
  ];

  return { config };
}
